import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Upload } from 'lucide-react';
import { Avatar } from 'primereact/avatar';

interface AvatarUploadProps {
  value?: string | null; // Image URL or base64 string
  onChange?: (file: File) => void;
  onRemove?: () => void;
  size?: number; // Size in pixels (default: 64)
  disabled?: boolean;
  className?: string;
  accept?: string; // File types to accept (default: image/*)
  maxSizeBytes?: number; // Max file size in bytes (default: 5MB)
}

const AvatarUpload: React.FC<AvatarUploadProps> = ({
  value,
  onChange,
  onRemove,
  size = 48,
  disabled = false,
  className = '',
  accept = 'image/*',
  maxSizeBytes = 5 * 1024 * 1024, // 5MB default
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [preview, setPreview] = useState<string | null>(value || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update preview when value prop changes
  useEffect(() => {
    setPreview(value || null);
  }, [value]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Please select an image file.');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size
    if (file.size > maxSizeBytes) {
      const maxSizeMB = (maxSizeBytes / (1024 * 1024)).toFixed(2);
      console.error(`File too large. Maximum size is ${maxSizeMB}MB.`);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Create preview and call onChange
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
      onChange?.(file);
    };
    reader.readAsDataURL(file);
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        className={`relative cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
      >
        <Avatar
          className="bg-white"
          style={{ width: `${size}px`, height: `${size}px` }}
        >
          {preview ? (
            <img
              src={preview}
              alt="Avatar"
              className="w-full h-full object-contain rounded-md bg-white"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-md">
              <Upload className="w-6 h-6 text-gray-400" />
            </div>
          )}
        </Avatar>

        {/* Edit overlay on hover */}
        {isHovered && !disabled && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md"
            style={{ width: `${size}px`, height: `${size}px` }}
          >
            <Pencil className="w-5 h-5 text-white" />
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default AvatarUpload;
